import React from "react";
import { useTranslation } from "react-i18next";
import { ApiHook } from "../../hooks/apiHook";

const RpsStatusDetails = () => {
  const { t, i18n } = useTranslation();
  const rpsQuery = ApiHook.CallRpsStatusDetails();
  const data = rpsQuery?.data;

  const lang = i18n.language;
  const getName = (item) => (lang === "ar" ? item.nameAr : item.nameEn);

  if (rpsQuery.isLoading) {
    return (
      <>
        <div className="page_head_top">{t("rps_status_details")}</div>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <div className="page_head_top">{t("rps_status_details")}</div>
        <div className="ranking-box">
          <div className="p-4 text-center text-muted">
            <i className="fa fa-info-circle fa-2x mb-2 d-block"></i>
            {t("rps_not_available")}
          </div>
        </div>
      </>
    );
  }

  const statusName = getName(data.current);

  return (
    <>
      <div className="page_head_top">{t("rps_status_details")}</div>

      {/* Current Status + PV Section */}
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="ranking-box h-100">
            <div className="joinings_viewBox_head">
              <h5>{t("rps_current_status")}</h5>
            </div>
            <div className="p-3 text-center">
              <span
                className={`badge fs-5 ${
                  data.current.isBaseline ? "bg-secondary" : "bg-success"
                }`}
              >
                {statusName || data.current.slug}
              </span>
              <div className="mt-3">
                <small className="text-muted d-block">{t("rps_current_tier")}</small>
                <span className="fw-bold fs-4">T{data.current.tier}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="ranking-box h-100">
            <div className="joinings_viewBox_head">
              <h5>{t("rps_your_pv")}</h5>
            </div>
            <div className="p-3">
              <div className="mb-3">
                <small className="text-muted">{t("rps_direct_pv")}</small>
                <h4 className="fw-bold text-primary mb-0">
                  {parseFloat(data.directPv).toLocaleString()}
                </h4>
              </div>
              <div>
                <small className="text-muted">{t("rps_group_pv")}</small>
                <h4 className="fw-bold text-info mb-0">
                  {parseFloat(data.groupPv).toLocaleString()}
                </h4>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="ranking-box h-100">
            <div className="joinings_viewBox_head">
              <h5>{t("rps_commission_impact")}</h5>
            </div>
            <div className="p-3">
              <div className="row g-2">
                {[
                  { key: "levelCommission", label: t("rps_factor_level") },
                  { key: "poolWeight", label: t("rps_factor_pool") },
                  { key: "infinityWeight", label: t("rps_factor_infinity") },
                  { key: "rankBonus", label: t("rps_factor_rank") },
                ].map(({ key, label }) => (
                  <div className="col-6" key={key}>
                    <div className="border rounded p-2 text-center">
                      <small className="text-muted d-block">{label}</small>
                      <span
                        className={`fw-bold ${
                          data.factors[key] < 1 ? "text-danger" : "text-success"
                        }`}
                      >
                        {(data.factors[key] * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                ))}
                {data.factors.directCommissionBonus && (
                  <div className="col-12 mt-2">
                    <div
                      className={`border rounded p-2 text-center ${
                        data.factors.directCommissionBonus.enabled
                          ? "border-warning bg-warning bg-opacity-10"
                          : ""
                      }`}
                    >
                      <small className="text-muted d-block">
                        {t("rps_direct_bonus")}
                      </small>
                      {data.factors.directCommissionBonus.enabled ? (
                        <span className="fw-bold text-warning">
                          +{data.factors.directCommissionBonus.percentage}%
                        </span>
                      ) : (
                        <span className="fw-bold text-muted">
                          {t("rps_bonus_inactive")}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Next Tier Progress */}
      {data.next && (
        <div className="ranking-box mb-4">
          <div className="joinings_viewBox_head">
            <h5>
              {t("rps_next_level")}: {getName(data.next) || data.next.slug} (T{data.next.tier})
            </h5>
          </div>
          <div className="p-3">
            <div className="row">
              <div className="col-md-6 mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <small>{t("rps_direct_pv")}</small>
                  <small className="text-muted">
                    {parseFloat(data.directPv).toLocaleString()} / {parseFloat(data.next.minDirectPv).toLocaleString()}
                  </small>
                </div>
                <div className="progress" style={{ height: "12px" }}>
                  <div
                    className="progress-bar bg-primary"
                    style={{
                      width: `${Math.min(
                        100,
                        (parseFloat(data.directPv) /
                          Math.max(parseFloat(data.next.minDirectPv), 1)) *
                          100
                      )}%`,
                    }}
                  />
                </div>
                <small className="text-muted">
                  {parseFloat(data.next.remainingDirectPv).toLocaleString()} {t("rps_remaining")}
                </small>
              </div>
              <div className="col-md-6 mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <small>{t("rps_group_pv")}</small>
                  <small className="text-muted">
                    {parseFloat(data.groupPv).toLocaleString()} / {parseFloat(data.next.minGroupPv).toLocaleString()}
                  </small>
                </div>
                <div className="progress" style={{ height: "12px" }}>
                  <div
                    className="progress-bar bg-info"
                    style={{
                      width: `${Math.min(
                        100,
                        (parseFloat(data.groupPv) /
                          Math.max(parseFloat(data.next.minGroupPv), 1)) *
                          100
                      )}%`,
                    }}
                  />
                </div>
                <small className="text-muted">
                  {parseFloat(data.next.remainingGroupPv).toLocaleString()} {t("rps_remaining")}
                </small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Tiers Ladder */}
      {data.allTiers && data.allTiers.length > 0 && (
        <div className="ranking-box mb-4">
          <div className="joinings_viewBox_head">
            <h5>{t("rps_tier_ladder")}</h5>
          </div>
          <div className="p-3">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>{t("rps_tier")}</th>
                    <th>{t("rps_status_name")}</th>
                    <th className="text-end">{t("rps_min_direct_pv")}</th>
                    <th className="text-end">{t("rps_min_group_pv")}</th>
                    <th className="text-center">{t("rps_factor_level")}</th>
                    <th className="text-center">{t("rps_factor_pool")}</th>
                    <th className="text-center">{t("rps_factor_infinity")}</th>
                    <th className="text-center">{t("rps_factor_rank")}</th>
                    <th className="text-center">{t("rps_direct_bonus")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.allTiers.map((tier) => (
                    <tr
                      key={tier.id}
                      className={tier.isCurrent ? "table-primary fw-bold" : ""}
                    >
                      <td>
                        T{tier.tier}
                        {tier.isCurrent && (
                          <i className="fa fa-check-circle text-success ms-1"></i>
                        )}
                      </td>
                      <td>{getName(tier) || tier.slug}</td>
                      <td className="text-end">
                        {tier.minDirectPv.toLocaleString()}
                      </td>
                      <td className="text-end">
                        {tier.minGroupPv.toLocaleString()}
                      </td>
                      <td className="text-center">
                        <span className={tier.levelCommissionFactor < 1 ? "text-danger" : "text-success"}>
                          {(tier.levelCommissionFactor * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="text-center">
                        <span className={tier.poolWeightFactor < 1 ? "text-danger" : "text-success"}>
                          {(tier.poolWeightFactor * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="text-center">
                        <span className={tier.infinityWeightFactor < 1 ? "text-danger" : "text-success"}>
                          {(tier.infinityWeightFactor * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="text-center">
                        <span className={tier.rankBonusFactor < 1 ? "text-danger" : "text-success"}>
                          {(tier.rankBonusFactor * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="text-center">
                        {tier.directCommissionBonusEnabled ? (
                          <span className="badge bg-warning text-dark">
                            +{tier.directCommissionBonusPercentage}%
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Period Info */}
      <div className="ranking-box">
        <div className="p-3">
          <div className="row text-center">
            <div className="col-md-4">
              <small className="text-muted d-block">{t("rps_period_start")}</small>
              <span className="fw-bold">
                {data.periodStart
                  ? new Date(data.periodStart).toLocaleDateString()
                  : "—"}
              </span>
            </div>
            <div className="col-md-4">
              <small className="text-muted d-block">{t("rps_period_end")}</small>
              <span className="fw-bold">
                {data.periodEnd
                  ? new Date(data.periodEnd).toLocaleDateString()
                  : "—"}
              </span>
            </div>
            <div className="col-md-4">
              <small className="text-muted d-block">{t("rps_last_evaluated")}</small>
              <span className="fw-bold">
                {data.evaluatedAt
                  ? new Date(data.evaluatedAt).toLocaleDateString()
                  : "—"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RpsStatusDetails;
